import type { UserRole } from '@today/types'
import { PlaneId, planeTargetsForRole } from '../usePlaneTargets'

/**
 * The three authenticated planes ordered widest → narrowest (AD-4): Platform is the
 * widest, Workspace the narrowest. "Narrowing" (a `step`) is moving from a wider
 * plane to a strictly narrower one. The ordinal is the index in this array.
 */
const PLANE_ORDER: readonly PlaneId[] = [PlaneId.Platform, PlaneId.TenantAdmin, PlaneId.Workspace]

/** The ordinal of a plane in the widest→narrowest order (lower = wider). */
function planeOrdinal(plane: PlaneId): number {
	return PLANE_ORDER.indexOf(plane)
}

/**
 * The viewer's natural / home plane: the widest plane their role can reach. Reuses
 * {@link planeTargetsForRole}, whose list is already ordered widest-first, so the
 * first entry is the home plane (SUPER_ADMIN → Platform, ORG_ADMIN → Tenant Admin,
 * USER → Workspace).
 */
export function homePlaneForRole(role: UserRole): PlaneId {
	return planeTargetsForRole(role)[0].id as PlaneId
}

/**
 * Maps a route path to the plane it belongs to — the inverse of `planeBasePath`:
 * `/admin*` → Platform, `/t/:slug/admin*` → Tenant Admin, everything else →
 * Workspace. Pure (no router), so it is unit-testable over any path.
 */
export function planeForPath(pathname: string): PlaneId {
	if (pathname === '/admin' || pathname.startsWith('/admin/')) return PlaneId.Platform
	if (/^\/t\/[^/]+\/admin(\/|$)/.test(pathname)) return PlaneId.TenantAdmin
	return PlaneId.Workspace
}

/**
 * A `step` band descriptor: a higher-role viewer is viewing a plane narrower than
 * their home plane. Carries the viewer's home plane so the shell's "Back" action can
 * navigate there.
 */
export interface StepBand {
	mode: 'step'
	/** The viewer's natural plane, the target of the Back action. */
	home: PlaneId
	/** The narrower plane currently being viewed. */
	route: PlaneId
}

/**
 * Derives the `step` context band from the viewer's home plane and the plane of the
 * current route — pure client state (frontend-architecture.md §2): it needs no
 * backend and no impersonation capability.
 *
 * Returns a {@link StepBand} only when `route` is **strictly narrower** than `home`
 * (the viewer is narrowing into a plane below their natural one); returns `null` when
 * the route plane equals or is wider than the home plane (no narrowing, no band).
 *
 * @remarks `enter`/`actas` are never produced here — those are server-authoritative
 * and blocked on AD-5 (ADR-0064). This derivation only ever yields `step` or `null`.
 */
export function deriveStepBand(home: PlaneId, route: PlaneId): StepBand | null {
	return planeOrdinal(route) > planeOrdinal(home) ? { mode: 'step', home, route } : null
}
